import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, Typography } from "@mui/material";
import { useRef, useState } from "react";

interface DropZoneProps {
  accept: string;
  label: string;
  file: File | null;
  onFile: (f: File | null) => void;
}

export function DropZone({ accept, label, file, onFile }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) onFile(f);
      }}
      sx={(t) => ({
        display: "block",
        width: "100%",
        bgcolor: dragOver ? "accent.softer" : "surface.raised",
        border: "1px dashed",
        borderColor: dragOver ? "primary.main" : "border.default",
        borderRadius: 2,
        p: 2,
        cursor: "pointer",
        textAlign: "center",
        font: "inherit",
        color: "inherit",
        transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}`,
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: "accent.softer",
        },
      })}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
      <CloudUploadIcon
        sx={{
          color: file ? "primary.light" : "text.disabled",
          fontSize: 26,
          mb: 0.5,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.8125rem",
          color: file ? "primary.light" : "text.secondary",
          fontWeight: file ? 500 : 400,
        }}
      >
        {file ? file.name : label}
      </Typography>
      {file && (
        <Typography
          sx={{ fontSize: "0.7rem", color: "text.disabled", mt: 0.25 }}
        >
          {(file.size / 1024).toFixed(1)} KB · click to change
        </Typography>
      )}
    </Box>
  );
}
